'use client';

import React from 'react';
import Link from 'next/link';
import { 
  ChevronRight, 
  Globe, 
  Users, 
  FilePlus, 
  UserSearch, 
  ArrowLeft,
  Compass,
  Video
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { BangladeshForm } from '@/doi-tac-module/components/bangladesh';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { useRouter } from 'next/navigation';
import { cn } from '@/doi-tac-module/lib/utils';

export default function QuyTrinhHubPage() {
  const { language } = useLanguage();
  const router = useRouter();

  const hubItems = [
    {
      id: "recruitment",
      title: language === 'vi' ? "Lộ trình ứng tuyển" : "Application Roadmap",
      description: language === 'vi' ? "Quy trình chuyển việc và hỗ trợ hồ sơ dành cho ứng viên Tokutei." : "Job transition process and profile support for Tokutei candidates.",
      icon: <Users className="h-8 w-8 text-[#19A6DF]" />,
      href: "/quy-trinh/quy-trinh-tuyen-dung",
      color: "from-[#19A6DF]/10 to-[#19A6DF]/5",
      borderColor: "border-[#19A6DF]/20",
      iconBg: "bg-[#19A6DF]/10",
      isHighlight: true
    },
    {
      id: "quickPost",
      title: language === 'vi' ? "Hoàn thiện hồ sơ" : "Profile Completion",
      description: language === 'vi' ? "Hướng dẫn cách chuẩn bị hồ sơ chuyên nghiệp và ghi điểm với nhà tuyển dụng." : "Guide on preparing a professional profile to impress employers.",
      icon: <FilePlus className="h-8 w-8 text-[#AFC536]" />,
      href: "/quy-trinh/dang-viec-lam-nhanh",
      color: "from-[#AFC536]/10 to-[#AFC536]/5",
      borderColor: "border-[#AFC536]/20",
      iconBg: "bg-[#AFC536]/10",
      isHighlight: true
    },
    {
      id: "quickSearch",
      title: language === 'vi' ? "Tìm kiếm việc làm" : "Job Search",
      description: language === 'vi' ? "Tiếp cận danh sách đơn hàng Tokutei mới nhất và phù hợp với năng lực." : "Access the latest Tokutei job listings that match your skills.",
      icon: <UserSearch className="h-8 w-8 text-[#F2B92A]" />,
      href: "/quy-trinh/tim-ung-vien-nhanh",
      color: "from-[#F2B92A]/10 to-[#F2B92A]/5",
      borderColor: "border-[#F2B92A]/20",
      iconBg: "bg-[#F2B92A]/10",
      isHighlight: true
    },
    {
      id: "interviewTraining",
      title: language === 'vi' ? "Luyện phỏng vấn AI" : "AI Interview Practice",
      description: language === 'vi' ? "Công nghệ AI giúp bạn luyện tập phản xạ và phát âm tiếng Nhật tự tin nhất." : "AI technology helps you practice reflexes and Japanese pronunciation confidently.",
      icon: <Video className="h-8 w-8 text-[#8B5CF6]" />,
      href: "/quy-trinh/quy-trinh-dao-tao-phong-van",
      color: "from-[#8B5CF6]/10 to-[#8B5CF6]/5",
      borderColor: "border-[#8B5CF6]/20",
      iconBg: "bg-[#8B5CF6]/10",
      isHighlight: true
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-slate-50/50 animate-in fade-in duration-500 will-change-opacity">
      {/* Header Section */}
      <section className="w-full py-16 lg:py-24 bg-[#19A6DF] bg-gradient-to-br from-[#19A6DF] to-[#0D8DC8] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,rgba(255,255,255,0),rgba(255,255,255,0.5))] opacity-10"></div>
        <div className="container relative z-10 mx-auto px-4 md:px-6">
          <div className="max-w-3xl space-y-4 animate-in fade-in slide-in-from-top-10 duration-700">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-1 text-sm font-bold backdrop-blur-md border border-white/30">
              <Compass className="h-4 w-4" />
              {language === 'vi' ? "Dành cho ứng viên Tokutei" : "For Tokutei Candidates"}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-snug md:leading-tight">
              {language === 'vi' ? "Lộ trình chuyển việc và hỗ trợ Tokutei" : "Job Transition & Tokutei Support Roadmap"}
            </h1>
            <p className="text-lg text-white/90 leading-relaxed max-w-2xl font-medium">
              {language === 'vi' 
                ? "Tổng hợp các quy trình chuẩn giúp ứng viên Tokutei Việt Nam tại Nhật Bản chuẩn bị hồ sơ, luyện phỏng vấn và kết nối việc làm hiệu quả nhất." 
                : "Comprehensive guide to help Vietnamese Tokutei candidates in Japan prepare profiles, practice interviews, and connect with jobs effectively."}
            </p>
          </div>
        </div>
        
        {/* Abstract Shapes */}
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/4 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>
      </section>

      {/* Main Content - List of Processes */}
      <section className="container mx-auto px-4 py-16 md:px-6 -mt-10 relative z-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {hubItems.map((item, index) => (
            <Link key={index} href={item.href} className="group">
              <Card className={cn(
                "h-full border-2 transition-all duration-300 hover:shadow-xl hover:border-transparent bg-white rounded-3xl overflow-hidden relative",
                item.borderColor
              )}>
                <div className={cn(
                  "absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300",
                  item.color
                )}></div>
                <CardContent className="p-8 relative z-10 flex flex-col h-full">
                  <div className={cn(
                    "w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300",
                    item.iconBg
                  )}>
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4 group-hover:text-slate-900 leading-tight">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 mb-6 font-medium text-sm leading-relaxed">
                    {item.description}
                  </p>
                  <div className="mt-auto pt-4 flex items-center text-sm font-bold text-slate-500 group-hover:text-slate-900 transition-colors">
                    {language === 'vi' ? "Xem quy trình chi tiết" : "View detailed process"}
                    <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Back Button */}
        <div className="mt-12 text-center">
          <Button 
            variant="ghost" 
            onClick={() => router.push('/')}
            className="text-slate-500 hover:text-slate-900 font-bold group"
          >
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            {language === 'vi' ? "Quay lại trang chủ" : "Back to home"}
          </Button>
        </div>
      </section>
    </div>
  );
}
